/**
 * A list of things as a table: servers, instances, revisions.
 *
 * On a phone a row of five or six columns does not fit, and scrolling the table sideways hides
 * the columns that matter (the state, why). Below 40rem every row becomes a card instead: the
 * first cell as its title, the others one per line, each after the heading of its column. The
 * headings reach the cells as `data-label`, written by {@link TableLabels}.
 */
import { css } from 'lit';

import { narrow } from '../media.js';

export const tableStyles = css`
  .table-wrap {
    overflow-x: auto;
    border: var(--border-width) solid var(--border);
    border-radius: var(--radius);
    background: var(--surface-raised);
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--font-size-ui);
  }

  th,
  td {
    padding: var(--table-cell-padding);
    text-align: left;
    vertical-align: middle;
    border-bottom: var(--border-width) solid var(--border);
    white-space: nowrap;
  }

  th {
    background: var(--surface);
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-weight: var(--font-weight-semibold);
    font-size: var(--font-size-2xs);
    text-transform: uppercase;
    letter-spacing: var(--tracking-widest);
    border-bottom: var(--rule-width) solid var(--text);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr {
    transition: background-color var(--duration-fast);
  }

  tbody tr:hover td {
    background: var(--row-hover);
  }

  tbody tr:hover td:first-child {
    box-shadow: inset var(--marker-width) 0 0 var(--accent);
  }

  td.wrap {
    white-space: normal;
  }

  td.num,
  th.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  tr.current td {
    background: var(--tone-progress-soft);
  }

  tr.current td:first-child {
    box-shadow: inset var(--marker-width) 0 0 var(--tone-progress);
  }

  @media ${narrow} {
    .table-wrap {
      overflow-x: visible;
    }

    table,
    tbody,
    tr,
    td {
      display: block;
    }

    /* Out of sight, still read out: the cards carry the headings as labels. */
    thead {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip-path: inset(50%);
      white-space: nowrap;
    }

    tbody tr {
      padding: var(--space-3) var(--space-4);
      border-bottom: var(--border-width) solid var(--border);
    }

    tbody tr:last-child {
      border-bottom: none;
    }

    td {
      padding: var(--space-0) 0;
      border-bottom: none;
      white-space: normal;
      text-align: right;
      overflow-wrap: anywhere;
    }

    /* Floated, so that a cell of several pieces (a badge and a date, "#2 → #3") stays one line. */
    td[data-label]:not([data-label=''])::before {
      content: attr(data-label);
      float: left;
      margin-right: var(--space-3);
      color: var(--text-muted);
      font-family: var(--font-mono);
      font-weight: var(--font-weight-semibold);
      font-size: var(--font-size-2xs);
      line-height: var(--line-height-loose);
      text-transform: uppercase;
      letter-spacing: var(--tracking-widest);
      text-align: left;
    }

    /* The title of the card; and cells with no heading (actions, a row of a table without one). */
    td:first-child,
    td:not([data-label]),
    td[data-label=''] {
      text-align: left;
    }

    td:first-child {
      padding-bottom: var(--space-1);
      font-weight: var(--font-weight-semibold);
    }

    td:first-child::before {
      display: none;
    }

    /* Prose (why, a description): under its label, not squeezed beside it. */
    td.wrap {
      text-align: left;
    }

    td.wrap::before {
      float: none;
      display: block;
    }

    td.num {
      text-align: right;
    }

    tbody tr:hover td,
    tr.current td {
      background: none;
    }

    tbody tr:hover td:first-child,
    tr.current td:first-child {
      box-shadow: none;
    }

    tbody tr:hover {
      background: var(--row-hover);
      box-shadow: inset var(--marker-width) 0 0 var(--accent);
    }

    tr.current {
      background: var(--tone-progress-soft);
      box-shadow: inset var(--marker-width) 0 0 var(--tone-progress);
    }
  }
`;
